import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { portalApi, formatPortalError } from "@/lib/portalApi";
import Spade from "@/components/Spade";
import { PORTAL } from "@/constants/testIds";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const navigate = useNavigate();
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (password.length < 6) {
            setError("Password must be at least 6 characters.");
            return;
        }
        if (password !== confirm) {
            setError("Passwords do not match.");
            return;
        }
        setLoading(true);
        try {
            await portalApi.post("/portal/reset-password", { token, password });
            toast.success("Password updated. Please log in.");
            navigate("/portal", { replace: true });
        } catch (err) {
            setError(formatPortalError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            data-testid={PORTAL.resetRoot}
            className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden bg-[#2a0303]"
        >
            <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#4A0505] via-[#3B0404] to-[#0d0000]" />
            <div className="absolute left-[-6%] bottom-[-4%] opacity-[0.05]">
                <Spade size={520} />
            </div>

            <motion.form
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                onSubmit={submit}
                className="w-full max-w-md surface-glass rounded-3xl border border-[var(--border-subtle)] p-8 md:p-10"
            >
                <div className="flex items-center gap-2 mb-8">
                    <Spade size={24} />
                    <span className="font-display text-xl text-[var(--text-primary)]">
                        Aestherium<span className="text-[var(--accent-gold)]">.</span>
                    </span>
                </div>

                <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent-gold)] mb-3">
                    Set New Password
                </p>
                <h2 className="font-display text-4xl font-bold text-[var(--text-primary)] mb-8">
                    Choose a new password.
                </h2>

                {!token && (
                    <p className="mb-5 text-sm text-red-300 bg-red-900/30 border border-red-500/30 rounded-lg px-4 py-3">
                        Missing reset token. Please use the link from your email.
                    </p>
                )}

                <label className="block text-xs uppercase tracking-[0.25em] text-[var(--accent-champagne)]/70 mb-2">
                    New Password
                </label>
                <input
                    data-testid={PORTAL.resetPassword}
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full mb-5 bg-transparent border border-[var(--border-subtle)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-gold)]"
                />

                <label className="block text-xs uppercase tracking-[0.25em] text-[var(--accent-champagne)]/70 mb-2">
                    Confirm Password
                </label>
                <input
                    data-testid={PORTAL.resetConfirm}
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    className="w-full mb-6 bg-transparent border border-[var(--border-subtle)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-gold)]"
                />

                {error && (
                    <p className="mb-5 text-sm text-red-300 bg-red-900/30 border border-red-500/30 rounded-lg px-4 py-3">
                        {error}
                    </p>
                )}

                <button
                    data-testid={PORTAL.resetSubmit}
                    type="submit"
                    disabled={loading || !token}
                    className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-[var(--accent-gold)] text-[var(--brand-dark)] font-semibold uppercase tracking-[0.2em] text-sm hover:bg-[var(--accent-champagne)] transition-colors disabled:opacity-70"
                >
                    {loading ? "Updating…" : "Update password"}
                    <ArrowRight size={16} />
                </button>

                <Link
                    to="/portal"
                    className="mt-6 block text-center text-xs text-[var(--text-secondary)] hover:text-[var(--accent-champagne)]"
                >
                    ← Back to login
                </Link>
            </motion.form>
        </div>
    );
}
